import React from 'react';
import { Platform, Pressable, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Colors, Radii, Shadows, LAYOUT } from '../constants';

interface FabProps {
  onPress: () => void;
  /** `accent` (slate, default) or `grey` (sage) */
  variant?: 'accent' | 'grey';
  style?: ViewStyle;
}

/**
 * Mobile floating "+" (design v2): 56px, panel radius (12), accent fill with the soft
 * green FAB shadow. Sits bottom-right, above the TabBar. Hover/press darkens
 * the fill.
 */
export const Fab: React.FC<FabProps> = ({ onPress, variant = 'accent', style }) => {
  const { t } = useTranslation();
  const darkFill = variant === 'grey' ? Colors.fabGreyDark : Colors.fabAccentDark;

  return (
    <Pressable
      style={({ pressed, hovered }: any) => [
        styles.fab,
        variant === 'grey' && styles.fabGrey,
        (hovered || pressed) && { backgroundColor: darkFill },
        pressed && styles.pressed,
        style,
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('navigation.addListing')}
      testID="fab-add-listing"
    >
      <Ionicons name="add" size={30} color={Colors.white} />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  fab: {
    position: 'absolute',
    bottom: LAYOUT.fab.bottom,
    right: LAYOUT.fab.right,
    width: LAYOUT.fab.size,
    height: LAYOUT.fab.size,
    borderRadius: Radii.fab,
    backgroundColor: Colors.fabAccent,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    ...Shadows.fabSoft,
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' } as any),
  },
  fabGrey: {
    backgroundColor: Colors.fabGrey,
  },
  pressed: {
    transform: [{ scale: 0.98 }],
  },
});
